import { useEffect, useState } from 'react';
import useBookings from './useBookings';
import { listStays } from '../../services/schedule';

const fmt = (date) => new Date(date).toLocaleDateString(undefined, { day: 'numeric', month: 'short', year: 'numeric' });

export default function OverviewPanel({ roomCount, rooms = [] }) {
  const { bookings: allBookings, error, loading } = useBookings();
  const [allStays, setAllStays] = useState([]);
  const [scheduleError, setScheduleError] = useState('');
  const [period, setPeriod] = useState('1');
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');

  useEffect(() => {
    listStays().then(setAllStays).catch((reason) => setScheduleError(reason.message));
  }, []);

  const periodStart = new Date();
  periodStart.setMonth(periodStart.getMonth() - Number(period === 'custom' ? 0 : period));
  const startDate = period === 'custom' ? from : periodStart.toISOString().slice(0, 10);
  const endDate = period === 'custom' ? to : new Date().toISOString().slice(0, 10);
  const inRange = (date) => Boolean(date && startDate && endDate && date.slice(0, 10) >= startDate && date.slice(0, 10) <= endDate);
  const bookings = allBookings.filter((b) => inRange(b.created_at));
  const stays = allStays.filter((stay) => inRange(stay.check_in));
  const today = new Date().toISOString().slice(0, 10);
  const count = (status) => bookings.filter((b) => b.status === status).length;
  const upcoming = bookings.filter((b) => b.status === 'confirmed' && b.check_in >= today).length;
  const guests = bookings.filter((b) => b.status !== 'cancelled').reduce((sum, b) => sum + (b.guests || 0), 0);
  const stats = [
    ['Total bookings', bookings.length],
    ['Pending', count('pending')],
    ['Confirmed', count('confirmed')],
    ['Cancelled', count('cancelled')],
    ['Upcoming stays', upcoming],
    ['Guests expected', guests],
    ['Rooms listed', roomCount]
  ];

  if (loading) return <div className="glass-card">Loading overview…</div>;

  return (
    <div className="glass-stack">
      {error && <p className="admin-warning" role="alert">{error}</p>}
      {scheduleError && <p className="admin-warning" role="alert">{scheduleError}</p>}
      <div className="glass-card">
        <h2 className="glass-title">Reporting period</h2>
        <div className="filter-pills">
          {[['1', '1 month'], ['3', '3 months'], ['6', '6 months'], ['12', '12 months'], ['custom', 'Custom dates']].map(([value, label]) => (
            <button type="button" key={value} aria-pressed={period === value} onClick={() => setPeriod(value)}>{label}</button>
          ))}
        </div>
        {period === 'custom' && (
          <div className="schedule-fields">
            <label className="form-field"><span>From</span><input type="date" value={from} onChange={(event) => setFrom(event.target.value)} /></label>
            <label className="form-field"><span>To</span><input type="date" min={from} value={to} onChange={(event) => setTo(event.target.value)} /></label>
          </div>
        )}
      </div>
      <div className="stat-grid">
        {stats.map(([label, value]) => (
          <div className="glass-card stat-card" key={label}><span>{label}</span><strong>{value}</strong></div>
        ))}
      </div>
      <div className="glass-card">
        <h2 className="glass-title">Bookings by room</h2>
        <div className="stat-grid">
          {rooms.map((room) => (
            <div className="stat-card" key={room.number}>
              <span>{room.title}</span>
              <strong>{stays.filter((stay) => stay.room_number === room.number).length}</strong>
              <small className="muted">scheduled stays · {bookings.filter((b) => b.room_name === room.title).length} requests</small>
            </div>
          ))}
        </div>
      </div>
      <div className="glass-card">
        <h2 className="glass-title">Latest requests</h2>
        {bookings.length === 0 && <p className="muted">No booking requests yet.</p>}
        <ul className="recent-list">
          {bookings.slice(0, 5).map((b) => (
            <li key={b.id}>
              <div><strong>{b.name}</strong><small>{fmt(b.check_in)} → {fmt(b.check_out)} · {b.guests} guest(s)</small></div>
              <span className={`badge badge-${b.status}`}>{b.status}</span>
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
}